import { IPet } from '@/shared/interfaces';
import { Modal } from '@/components';
import { R } from '@/features/profile/ProfileResources.resx';
import { PetSexEnum, PetTypeEnum } from '@/shared/enums';
import { useState } from 'react';
import { Select, Button, Input, FormErrorText } from '@/components';
import { Utils } from '@/shared/Utils';

export type PetModalProps = {
    pet: IPet;
    onClose: () => void;
};

const petTypeOptions = [
    { value: PetTypeEnum.Dog, label: R.PetTypeDog },
    { value: PetTypeEnum.Cat, label: R.PetTypeCat },
    { value: PetTypeEnum.Rabbit, label: R.PetTypeRabbit },
    { value: PetTypeEnum.Rodent, label: R.PetTypeRodent },
];

const petSexOptions = [
    { value: PetSexEnum.Female, label: R.PetSexFemale },
    { value: PetSexEnum.FemaleSterilized, label: R.PetSexFemaleSterilized },
    { value: PetSexEnum.Male, label: R.PetSexMale },
    { value: PetSexEnum.MaleNeutered, label: R.PetSexMaleNeutered },
    { value: PetSexEnum.Unknown, label: R.PetSexUnknown },
];

const months = [
    { value: 0, label: R.MonthJanuary },
    { value: 1, label: R.MonthFebruary },
    { value: 2, label: R.MonthMarch },
    { value: 3, label: R.MonthApril },
    { value: 4, label: R.MonthMay },
    { value: 5, label: R.MonthJune },
    { value: 6, label: R.MonthJuly },
    { value: 7, label: R.MonthAugust },
    { value: 8, label: R.MonthSeptember },
    { value: 9, label: R.MonthOctober },
    { value: 10, label: R.MonthNovember },
    { value: 11, label: R.MonthDecember },
];

const currentYear = new Date().getFullYear();
const maxYears = currentYear - 30;

const years = [
    ...Array.from(
        { length: currentYear - maxYears + 1 },
        (_, i) => maxYears + i,
    ).map((year) => ({ value: year, label: year.toString() })),
];

export const PetModal = ({ onClose, pet }: PetModalProps) => {
    const [selectedPetType, setSelectedPetType] = useState('');
    const [selectedPetSex, setSelectedPetSex] = useState('');
    const [selectedMonth, setSelectedMonth] = useState('');
    const [selectedYear, setSelectedYear] = useState('');

    const onCancel = () => onClose();

    const isEditing = pet != null;

    return (
        <Modal onClose={onCancel} title={R.AddPet}>
            <div className="flex h-full flex-col justify-between">
                <main>
                    <div className="flex flex-col justify-between gap-1">
                        <Input
                            label={R.PetName}
                            value={isEditing ? pet.Name : null}
                            name={`pet-modal-${R.PetName}`}
                            error={R.PleaseEnterPetName}
                        ></Input>

                        <div className="flex justify-between gap-2 py-2">
                            <div className="flex flex-col">
                                <p
                                    id="petTypeLabel"
                                    className="ds-text-highlighted my-0 py-1 text-lg font-bold text-grey-900"
                                >
                                    {R.PetType}
                                </p>
                                <div className="form-group">
                                    <Select
                                        prompt={R.SelectPetType}
                                        options={petTypeOptions}
                                        value={
                                            isEditing ? pet.Type.toString() : ''
                                        }
                                        onChange={(e) =>
                                            setSelectedPetType(e.target.value)
                                        }
                                    ></Select>
                                    <FormErrorText
                                        message={R.PleaseSelectPetType}
                                    />
                                </div>
                            </div>

                            <div className="flex w-full flex-col">
                                <p
                                    id="petSexLabel"
                                    className="ds-text-highlighted my-0 py-1 text-lg font-bold text-grey-900"
                                >
                                    {R.PetSex}
                                </p>
                                <div className="form-group">
                                    <Select
                                        className="w-full"
                                        prompt={R.SelectPetSex}
                                        options={petSexOptions}
                                        value={
                                            isEditing ? pet.Sex.toString() : ''
                                        }
                                        onChange={(e) =>
                                            setSelectedPetSex(e.target.value)
                                        }
                                    ></Select>
                                    <FormErrorText
                                        message={R.PleaseSelectPetSex}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="py-2">
                            <p className="ds-text-highlighted my-0 py-1 text-lg font-bold text-grey-900">
                                {R.PetBirthdate}
                            </p>
                            <div className="flex justify-between gap-2">
                                <div className="form-group w-1/2">
                                    <Select
                                        className="w-full"
                                        prompt={R.SelectMonth}
                                        options={months}
                                        value={
                                            isEditing
                                                ? Utils.formatMonth(
                                                      pet.Birthdate,
                                                  )
                                                : ''
                                        }
                                        onChange={(e) =>
                                            setSelectedMonth(e.target.value)
                                        }
                                    ></Select>
                                    <FormErrorText
                                        message={R.PleaseSelectMonth}
                                    />
                                </div>

                                <div className="form-group w-1/2">
                                    <Select
                                        className="w-full"
                                        prompt={R.SelectYear}
                                        options={years}
                                        value={
                                            isEditing
                                                ? Utils.formatYear(
                                                      pet.Birthdate,
                                                  )
                                                : ''
                                        }
                                        onChange={(e) =>
                                            setSelectedYear(e.target.value)
                                        }
                                    ></Select>
                                    <FormErrorText
                                        message={R.PleaseSelectYear}
                                    />
                                </div>
                            </div>
                            <div className="text-left text-sm text-grey-600">
                                {R.PetBirthdateHint}
                            </div>
                        </div>

                        <Input
                            label={R.PetWeight}
                            placeholder={R.PetWeightPlaceholder}
                            value={isEditing ? pet.WeightKg.toString() : null}
                            name={`pet-modal-${R.PetWeight}`}
                        ></Input>

                        <Input
                            label={R.PetRace}
                            placeholder={R.PetRacePlaceholder}
                            value={isEditing ? pet.Race : null}
                            name={`pet-modal-${R.PetRace}`}
                        ></Input>
                    </div>
                </main>

                <footer className="text-right">
                    <Button variant="primary">
                        {isEditing ? R.ConfirmEditPet : R.ConfirmAddPet}
                    </Button>
                </footer>
            </div>
        </Modal>
    );
};
